<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLayoutComponents } from '@/composables/layout.js';
import { useToast } from '@/composables/use-toast.js';
import { useStorage } from '@/composables/use-storage';
import { verifyEmail, resendVerificationEmail } from '@/services/authService.js';
import { getFriendlyErrorMessage, RESEND_VERIFICATION_THROTTLE_TOAST } from '@/utils/errorUtils.js';

const route = useRoute();
const router = useRouter();
const { PrimaryButton, ButtonLoading, Toast } = useLayoutComponents();
const { addToast } = useToast();
const { getUserInfo, saveUserInfo, isLoggedIn } = useStorage();

const loading = ref(true);
const success = ref(false);
const errorMessage = ref('');
const isResending = ref(false);

const canResend = computed(() => !success.value && isLoggedIn());

onMounted(async () => {
  const token = route.params.token;
  if (!token) {
    loading.value = false;
    errorMessage.value = 'This verification link is missing a token. Request a new verification email after logging in.';
    return;
  }

  try {
    const response = await verifyEmail(token);
    success.value = true;
    addToast(response?.message || 'Email verified successfully', 'success');

    const userInfo = getUserInfo();
    if (userInfo) {
      await saveUserInfo({ ...userInfo, email_verified: true });
    }
  } catch (error) {
    errorMessage.value = getFriendlyErrorMessage(
      error,
      'Unable to verify email. The link may be invalid or expired.',
    );
    addToast(errorMessage.value, 'error');
  } finally {
    loading.value = false;
  }
});

const handleResend = async () => {
  isResending.value = true;
  try {
    const response = await resendVerificationEmail();
    addToast(response?.message || 'Verification email sent', 'success');
  } catch (error) {
    addToast(
      error.response?.status === 429
        ? RESEND_VERIFICATION_THROTTLE_TOAST
        : getFriendlyErrorMessage(error, 'Unable to resend verification email'),
      'error',
    );
  } finally {
    isResending.value = false;
  }
};
</script>

<template>
  <Toast />
  <div class="verify-email">
    <div class="verify-email-card bg-echoes-background-black border border-echoes-border-gray">
      <div class="verify-email-card__header">
        <img src="/favicon.ico" alt="logo" class="verify-email-card__header-logo" />
        <h2 class="verify-email-card__header-title">Email verification</h2>
        <span class="verify-email-card__header-subtitle">
          Confirming your account email address
        </span>
      </div>

      <div class="verify-email-card__body">
        <div v-if="loading" class="text-center text-white/80">
          <ButtonLoading size="sm" text="Verifying email..." />
        </div>

        <div v-else-if="success" class="space-y-4 text-center">
          <p class="text-emerald-400 font-medium">Your email was verified successfully.</p>
          <PrimaryButton class="verify-email-card__button" @click="router.push('/explore')">
            Continue to Explore
          </PrimaryButton>
        </div>

        <div v-else class="space-y-4 text-center">
          <p class="text-red-400 font-medium">{{ errorMessage }}</p>
          <button
            v-if="canResend"
            type="button"
            class="w-full px-3 py-2 rounded-lg bg-amber-400 text-black text-sm font-semibold hover:bg-amber-300 transition-colors disabled:opacity-60"
            :disabled="isResending"
            @click="handleResend"
          >
            {{ isResending ? 'Sending...' : 'Resend verification email' }}
          </button>
          <PrimaryButton class="verify-email-card__button" @click="router.push('/login')">
            Back to Login
          </PrimaryButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.verify-email {
  @apply flex justify-center items-center h-[100dvh] w-screen px-4;
  background-image: linear-gradient(to bottom, #232323, #000000);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.verify-email-card {
  @apply w-full max-w-md rounded-2xl p-8 space-y-6;
}

.verify-email-card__header {
  @apply flex flex-col items-center text-center gap-2;
}

.verify-email-card__header-logo {
  @apply w-10 h-10 mb-2;
}

.verify-email-card__header-title {
  @apply text-2xl font-semibold text-white;
}

.verify-email-card__header-subtitle {
  @apply text-sm text-white/60;
}

.verify-email-card__body {
  @apply pt-2;
}

.verify-email-card__button {
  @apply w-full;
}
</style>
